import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalBody, Form, FormGroup, Label, Input } from "reactstrap";
import { Calendar } from "react-date-range";
import "react-calendar-timeline/lib/Timeline.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enGB, srLatn } from "date-fns/locale";
import { format } from "date-fns";
import { setHours, setMinutes, setSeconds, setMilliseconds } from "date-fns";
import { useLocation } from 'react-router-dom';
import { BsCaretDownFill } from 'react-icons/bs';
import "./modals.css"

import apiRequest from "../../api";
import TermsModal from "./TermsModalPDFJS";
import PricingModal from "./PricingModal";

const EditModal = ({
    isOpen,
    toggle,
    selectedRow,
    fetchReservations
}) => {
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        return localStorage.getItem('Language') || 'EN';
    });
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
    useEffect(() => {
        setSelectedLanguage(localStorage.getItem('Language') || 'EN');
    }, [isOpen]);
    const locale = selectedLanguage === 'RS' ? srLatn : enGB;
    const calendarRef = useRef(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const { pathname } = useLocation()


    // Define settings based on language
    const getSettings = () => {
        if (selectedLanguage === "EN") {
            return ["Manager (max 44)", "Theater (max 56)", "Training", "Custom"];
        } else {
            return ["Menadžerska (max 44)", "Bioskopska (max 56)", "Trening", "Prilagođeno"];
        }
    };

    const formatTime = (date) => {
        date = date instanceof Date ? date : new Date(date);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };
    const [formDetails, setFormDetails] = useState({
        date: selectedRow.start_time,
        start: formatTime(selectedRow.start_time),
        end: formatTime(selectedRow.end_time),
        name: selectedRow.name,
        username: selectedRow.username,
        seats: selectedRow.seats,
        id: selectedRow.id,
        notes: selectedRow.notes,
    });
    const [errors, setErrors] = useState({
        start: "",
        end: "",
        name: "",
        message: "",
        seats: "",
    });



    const toggleModal = (date) => {
        setErrors({
            start: "",
            end: "",
            name: "",
            message: "",
            seats: "",
        });
        setFormDetails({
            date: selectedRow.start_time,
            start: formatTime(selectedRow.start_time),
            end: formatTime(selectedRow.end_time),
            name: selectedRow.name,
            username: selectedRow.username,
            seats: selectedRow.seats,
            id: selectedRow.id,
            notes: selectedRow.notes,
        });
        toggle();
    };
    const toggleDropdown = () => {
        setDropdownOpen((prevState) => !prevState); // Toggle dropdown visibility
    };
    useEffect(() => {
        console.log("Selected row changed:", selectedRow);
        setFormDetails({
            date: selectedRow.start_time,
            start: formatTime(selectedRow.start_time),
            end: formatTime(selectedRow.end_time),
            name: selectedRow.name,
            username: selectedRow.username,
            seats: selectedRow.seats,
            id: selectedRow.id,
            notes: selectedRow.notes,
        });
    }, [selectedRow]);



    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {
            start: formDetails.start === "" ? (selectedLanguage === "EN" ? "This field is required" : "Ovo polje je obavezno") : "",
            end: formDetails.end === "" ? (selectedLanguage === "EN" ? "This field is required" : "Ovo polje je obavezno") : "",
            name: formDetails.name === "" ? (selectedLanguage === "EN" ? "This field is required" : "Ovo polje je obavezno") : "",
            seats: formDetails.seats === "" ? (selectedLanguage === "EN" ? "This field is required" : "Ovo polje je obavezno") : "",
        };
        setErrors({
            ...newErrors
        });

        if (newErrors.start || newErrors.end || newErrors.name || newErrors.seats) {
            return;
        }
        const startTime = `${format(formDetails.date, "yyyy-MM-dd")}T${formDetails.start}`;
        const endTime = `${format(formDetails.date, "yyyy-MM-dd")}T${formDetails.end}`;
        const translationMap = {
            "Manager (max 44)": "Manager (max 44)",
            "Theater (max 56)": "Theater (max 56)",
            "Menadžerska (max 44)": "Manager (max 44)",
            "Bioskopska (max 56)": "Theater (max 56)",
            "Training": "Trening",
            "Trening": "Training",
            "Custom": "Prilagođeno",
            "Prilagođeno": "Custom",
        };
        // Create the request body
        const requestBody = {
            reservationCode: formDetails.id,
            startTime: startTime,
            endTime: endTime,
            name: formDetails.name,
            notes: formDetails.notes || "",
            seats: translationMap[formDetails.seats],
            language: selectedLanguage,
        };

        // Make the POST request
        try {
            const token = sessionStorage.getItem("jwt"); // Retrieve JWT token from localStorage
            const response = await apiRequest('/api/reservations/editReservation', 'POST', requestBody, token);

            // Handle the response
            console.log("Reservation updated successfully:", response);
            fetchReservations();
            toggleModal(); // Close the modal after successful submission

        } catch (error) {
            try {
                // Parse the error message
                const errorData = JSON.parse(error.message);
                if (errorData.Body && errorData.Body.error) {
                    window.alert(errorData.Body.error); // Display the error message
                }
            } catch (parseError) {
                console.error("Failed to parse error message:", parseError);
            }
        }
    };

    const handleInputChange = (e, field) => {
        const { name, value } = e.target;

        setFormDetails((prevDetails) => ({
            ...prevDetails,
            [name]: value,
            ...(name === "start" && prevDetails.end < value ? { end: value } : {}), // Update "end" if necessary
        }));
    };

    const handleSettingSelect = (setting) => {
        setFormDetails((prevDetails) => ({
            ...prevDetails,
            seats: setting, // Update the selected setting
        }));
        setDropdownOpen(false); // Close the dropdown
    };
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target) // Check if click is outside
            ) {
                setIsCalendarOpen(false);
            }
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    return (
        <>
        <Modal isOpen={isOpen} toggle={toggleModal} centered className="custom-model">
            <ModalBody className="modal-content" style={{ backgroundColor: "#f5f5f5" }}>
                <Form>
                    {(pathname.includes("admin")) && (
                        <div className="row justify-content-center pt-2 mb-2">
                            <div className="col-md-6">
                                <FormGroup>
                                    <Label className="w-10 label">{selectedLanguage === "EN" ? "User:" : "Korisnik:"}</Label>
                                    <Label className="w-50 text-center label">
                                        {formDetails.username}
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>
                    )}
                    <div className="row justify-content-center mb-2" ref={calendarRef} >
                        <div className="col-md-6">
                            <FormGroup className="modal-date">
                                <Label>
                                    {selectedLanguage === "EN" ? "Date" : "Datum"}
                                </Label>
                                <Input
                                    type="text"
                                    className="form-control"
                                    value={format(formDetails.date, "dd.MM.yyyy")}
                                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                                    readOnly
                                    style={{ cursor: "pointer", borderRadius: 5 }}
                                />
                                <div className="calendar-container">
                                    {isCalendarOpen && (
                                        <Calendar
                                            date={formDetails.date}
                                            onChange={(date) => {
                                                setFormDetails((prevDetails) => ({
                                                    ...prevDetails,
                                                    date: date, // Update the date
                                                }));
                                                setIsCalendarOpen(false);
                                            }}
                                            locale={locale}
                                            color="rgba(13, 114, 72, 0.4)"
                                            className="calendar-container"
                                            style={{
                                                '--rdrDayToday-border-color': 'green',
                                            }}
                                        />
                                    )}
                                </div>
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <FormGroup>
                                <Label>
                                    {selectedLanguage === "EN" ? "Start" : "Početak"}
                                </Label>
                                <Input
                                    type="time"
                                    name="start"
                                    value={formDetails.start}
                                    onChange={handleInputChange}
                                    className={`form-control text-center ${errors.start ? "input-error" : ""}`}
                                />
                            </FormGroup>
                        </div>
                        <div className="col-md-6">
                            <FormGroup>
                                <Label>
                                    {selectedLanguage === "EN" ? "End" : "Kraj"}
                                </Label>
                                <Input
                                    type="time"
                                    name="end"
                                    value={formDetails.end}
                                    onChange={handleInputChange}
                                    className={`form-control text-center ${errors.end ? "input-error" : ""}`}
                                />
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-md-6">
                            <FormGroup>
                                <Label>
                                    {selectedLanguage === "EN" ? "Name" : "Ime"}
                                </Label>
                                <Input
                                    type="text"
                                    name="name"
                                    value={formDetails.name}
                                    onChange={handleInputChange}
                                    className={errors.name ? "input-error" : ""}
                                />
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-md-6">
                            <FormGroup>
                                <Label>
                                    {selectedLanguage === "EN" ? "Seating Arrangement" : "Postavka"}
                                </Label>
                                <div className={`custom-model form-control ${errors.seats ? "input-error" : ""}`} ref={dropdownRef}>
                                    <div
                                        className={`dropdown-seating`}
                                        style={{ cursor: "pointer" }}
                                        onClick={toggleDropdown}
                                    >
                                        <span
                                            style={{
                                                opacity: formDetails.seats ? 1 : 0.6, // Set opacity to 0.8 for placeholder, 1 for selected text
                                            }}
                                        >
                                            {formDetails.seats ? (formDetails.seats.includes('(') ? formDetails.seats.substring(0, formDetails.seats.indexOf(' (')) : formDetails.seats) : `${selectedLanguage === 'EN' ? "Select seating" : "Izaberite postavku"}`}
                                        </span>
                                        <span className="dropdown-arrow"><BsCaretDownFill fontSize={16} color='#888' /></span>
                                    </div>
                                    <div
                                        className={`dropdown-options ${dropdownOpen ? "open" : ""}`}
                                    >
                                        {getSettings().map((setting, index) => (
                                            <div
                                                key={index}
                                                className={`dropdown-option ${formDetails.seats === setting ? "selected" : ""
                                                    }`}
                                                onClick={() => handleSettingSelect(setting)}
                                            >
                                                {setting}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row justify-content-center mb-2">
                        <div className="col-12">
                            <FormGroup>
                                <Label>{selectedLanguage === "EN" ? "Note" : "Napomena"}</Label>
                                <textarea
                                    name="notes"
                                    className="form-control input-left"
                                    value={formDetails.notes}
                                    onChange={handleInputChange}
                                    rows="2"
                                    cols="50"
                                />
                            </FormGroup>
                        </div>
                    </div>
                    {errors.message && (
                        <div className="mb-3"
                            style={{
                                fontSize: "16px",
                                fontFamily: "jura-light",       // Text color
                                textAlign: "center",
                                color: "red",  // Centers the text horizontally
                            }}>
                            {errors.message}
                        </div>
                    )}
                    <div className="terms-pricing-links mb-3">
                        <div className="text-center">
                            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                                {selectedLanguage === "EN" 
                                    ? "By modifying your reservation, you agree to our:"
                                    : "Izmenom rezervacije se slažete sa našim:"
                                }
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                <span 
                                    onClick={() => setIsTermsModalOpen(true)}
                                    style={{ 
                                        color: '#0d7248', 
                                        cursor: 'pointer', 
                                        textDecoration: 'underline',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#16a085'}
                                    onMouseLeave={(e) => e.target.style.color = '#0d7248'}
                                >
                                    {selectedLanguage === "EN" ? "Terms & Conditions" : "Uslovima korišćenja"}
                                </span>
                                <span style={{ color: '#ccc' }}>|</span>
                                <span 
                                    onClick={() => setIsPricingModalOpen(true)}
                                    style={{ 
                                        color: '#0d7248', 
                                        cursor: 'pointer', 
                                        textDecoration: 'underline',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#16a085'}
                                    onMouseLeave={(e) => e.target.style.color = '#0d7248'}
                                >
                                    {selectedLanguage === "EN" ? "Pricing" : "Cenovnikom"}
                                </span>
                            </div>
                        </div>
                    </div>

                    <FormGroup>
                        <div className="row">
                            <div className="col-md-6 col-6 d-flex justify-content-center align-items-center">
                                <button
                                    className="model-btn-cancel btn btn-secondary py-1 px-5"
                                    onClick={toggleModal}
                                    type="button"
                                >
                                    {selectedLanguage === "EN" ? "Cancel" : "Otkaži"}
                                </button>
                            </div>
                            <div className="col-md-6 col-6 d-flex justify-content-center align-items-center">
                                <button
                                    className="model-btn btn btn-primary py-1 px-5"
                                    onClick={handleSubmit}
                                >
                                    {selectedLanguage === "EN" ? "Apply" : "Primeni"}
                                </button>
                            </div>
                        </div>
                    </FormGroup>
                </Form>
            </ModalBody>
        </Modal>
        <TermsModal 
            isOpen={isTermsModalOpen} 
            toggle={() => setIsTermsModalOpen(false)} 
        />
        <PricingModal 
            isOpen={isPricingModalOpen} 
            toggle={() => setIsPricingModalOpen(false)} 
        />
        </>
    )
}

export default EditModal;
